import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  TrophyIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  TicketIcon,
  SparklesIcon,
  UserGroupIcon,
  PhotoIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import DateInput from '../ui/DateInput';
import Switch from '../ui/Switch';
import ConfirmDialog from '../ui/ConfirmDialog';
import EmptyState from '../ui/EmptyState';
import { FullPageSkeleton } from '../ui/PageSkeleton';
import { useLanguage } from '../../contexts/LanguageContext';

const ease = [0.23, 1, 0.32, 1];
const STATES = ['affiliated', 'contacted', 'visited', 'completed'];
const PRIZE_TYPES = ['physical', 'cash'];

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');
const dateToIso = (value) => (value ? `${value}T00:00:00` : null);

const emptyPromotion = {
  title: '',
  subtitle: '',
  description: '',
  prize_type: 'physical',
  prize_name: '',
  prize_value_cents: '',
  image_url: '',
  required_referrals: 3,
  min_referral_state: 'affiliated',
  count_partners: true,
  is_active: true,
  exclusive_client_email: '',
};

const emptyCycle = { label: '', starts_at: '', ends_at: '', draw_at: '' };

function statusBadge(cycle) {
  if (cycle.status === 'drawn') return 'bg-secondary-100 text-secondary-600 ring-secondary-200';
  if (cycle.computed_status === 'open') return 'bg-success-100 text-success-700 ring-success-200';
  if (cycle.computed_status === 'due') return 'bg-error-100 text-error-700 ring-error-200';
  return 'bg-warning-100 text-warning-700 ring-warning-200';
}

function cycleStatusLabel(cycle, copy) {
  if (cycle.status === 'drawn') return copy.drawnLabel || 'Drawn';
  if (cycle.computed_status === 'open') return copy.open || 'Open';
  if (cycle.computed_status === 'due') return copy.due || 'Draw pending';
  return copy.scheduled || 'Scheduled';
}

function AdminPromotionsView() {
  const { t } = useLanguage();
  const copy = t.promotions || {};
  const [promotions, setPromotions] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [promoModal, setPromoModal] = useState({ open: false, mode: 'create', data: emptyPromotion, id: null });
  const [cycleModal, setCycleModal] = useState({ open: false, promotionId: null, data: emptyCycle, id: null });
  const [participantsModal, setParticipantsModal] = useState({ open: false, cycle: null, rows: [] });
  const [winnerModal, setWinnerModal] = useState({ open: false, cycle: null, clientId: '', reason: '' });
  const [confirm, setConfirm] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [promoRes, auditRes] = await Promise.all([
        axios.get('/api/admin/promotions', { withCredentials: true }),
        axios.get('/api/admin/promotions/audit', { withCredentials: true }),
      ]);
      setPromotions(promoRes.data?.promotions || []);
      setAudits(auditRes.data?.audits || []);
    } catch (error) {
      console.error('Error loading promotions:', error);
      toast.error(copy.loadError || 'Could not load promotions');
    } finally {
      setLoading(false);
    }
  }, [copy.loadError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const stats = useMemo(() => ({
    total: promotions.length,
    active: promotions.filter((p) => p.is_active).length,
    tickets: promotions.reduce((sum, p) => sum + (p.cycles || []).reduce((s, c) => s + (c.ticket_count || 0), 0), 0),
    winners: promotions.reduce((sum, p) => sum + (p.cycles || []).filter((c) => c.status === 'drawn' && c.winner_client_id).length, 0),
  }), [promotions]);

  const openCreatePromotion = () => setPromoModal({ open: true, mode: 'create', data: { ...emptyPromotion }, id: null });

  const openEditPromotion = (promotion) => setPromoModal({
    open: true,
    mode: 'edit',
    id: promotion.id,
    data: {
      title: promotion.title || '',
      subtitle: promotion.subtitle || '',
      description: promotion.description || '',
      prize_type: promotion.prize_type || 'physical',
      prize_name: promotion.prize_name || '',
      prize_value_cents: promotion.prize_value_cents ?? '',
      image_url: promotion.image_url || '',
      required_referrals: promotion.required_referrals ?? 3,
      min_referral_state: promotion.min_referral_state || 'affiliated',
      count_partners: Boolean(promotion.count_partners),
      is_active: Boolean(promotion.is_active),
      exclusive_client_email: promotion.exclusive_client_email || '',
    },
  });

  const savePromotion = async () => {
    const data = promoModal.data;
    if (!data.title?.trim() || !data.prize_name?.trim()) {
      toast.error(copy.requiredFields || 'Title and prize name are required');
      return;
    }
    setSaving(true);
    const body = {
      ...data,
      required_referrals: Number(data.required_referrals) || 1,
      prize_value_cents: data.prize_value_cents === '' ? null : Number(data.prize_value_cents),
    };
    try {
      if (promoModal.mode === 'edit') {
        await axios.put(`/api/admin/promotions/${promoModal.id}`, body, { withCredentials: true });
      } else {
        await axios.post('/api/admin/promotions', body, { withCredentials: true });
      }
      setPromoModal((prev) => ({ ...prev, open: false }));
      toast.success(copy.saved || 'Saved');
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || copy.saveError || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const deletePromotion = (promotion) => setConfirm({
    title: copy.deleteTitle || 'Delete promotion',
    message: (copy.deleteMessage || 'Delete "{title}"? This cannot be undone.')
      .replace('{title}', promotion.title),
    confirmLabel: copy.delete || 'Delete',
    onConfirm: async () => {
      try {
        await axios.delete(`/api/admin/promotions/${promotion.id}`, { withCredentials: true });
        toast.success(copy.deleted || 'Deleted');
        fetchAll();
      } catch (error) {
        toast.error(error?.response?.data?.error || copy.saveError || 'Could not delete');
      }
    },
  });

  const openCycleModal = (promotion, cycle = null) => setCycleModal({
    open: true,
    promotionId: promotion.id,
    id: cycle?.id || null,
    data: cycle
      ? {
          label: cycle.label || '',
          starts_at: toDateInput(cycle.starts_at),
          ends_at: toDateInput(cycle.ends_at),
          draw_at: toDateInput(cycle.draw_at),
        }
      : { ...emptyCycle },
  });

  const saveCycle = async () => {
    const data = cycleModal.data;
    if (!data.starts_at || !data.ends_at) {
      toast.error(copy.datesRequired || 'Start and end dates are required');
      return;
    }
    setSaving(true);
    const body = {
      label: data.label,
      starts_at: dateToIso(data.starts_at),
      ends_at: dateToIso(data.ends_at),
      draw_at: dateToIso(data.draw_at || data.ends_at),
    };
    try {
      if (cycleModal.id) {
        await axios.put(`/api/admin/promotions/cycles/${cycleModal.id}`, body, { withCredentials: true });
      } else {
        await axios.post(`/api/admin/promotions/${cycleModal.promotionId}/cycles`, body, { withCredentials: true });
      }
      setCycleModal((prev) => ({ ...prev, open: false }));
      toast.success(copy.saved || 'Saved');
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.error || copy.saveError || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const deleteCycle = (promotion, cycle) => setConfirm({
    title: copy.deleteCycleTitle || 'Delete period',
    message: (copy.deleteCycleMessage || 'Delete period "{label}"?').replace('{label}', cycle.label),
    confirmLabel: copy.delete || 'Delete',
    onConfirm: async () => {
      try {
        await axios.delete(`/api/admin/promotions/cycles/${cycle.id}`, { withCredentials: true });
        toast.success(copy.deleted || 'Deleted');
        fetchAll();
      } catch (error) {
        toast.error(error?.response?.data?.error || copy.saveError || 'Could not delete');
      }
    },
  });

  const openParticipants = async (cycle) => {
    try {
      const response = await axios.get(`/api/admin/promotions/cycles/${cycle.id}/participants`, { withCredentials: true });
      setParticipantsModal({ open: true, cycle, rows: response.data?.participants || [] });
    } catch (error) {
      toast.error(copy.loadError || 'Could not load');
    }
  };

  const forceDraw = (cycle) => setConfirm({
    title: copy.drawTitle || 'Draw winner now',
    message: (copy.drawMessage || 'Draw a winner for "{label}" now? This cannot be undone.')
      .replace('{label}', cycle.label),
    confirmLabel: copy.draw || 'Draw',
    onConfirm: async () => {
      try {
        await axios.post(`/api/admin/promotions/cycles/${cycle.id}/draw`, { reason: 'manual' }, { withCredentials: true });
        toast.success(copy.drawn || 'Winner drawn');
        fetchAll();
      } catch (error) {
        toast.error(error?.response?.data?.error || copy.saveError || 'Could not draw');
      }
    },
  });

  const openWinnerModal = async (cycle) => {
    try {
      const response = await axios.get(`/api/admin/promotions/cycles/${cycle.id}/participants`, { withCredentials: true });
      setWinnerModal({ open: true, cycle, clientId: '', reason: '', rows: response.data?.participants || [] });
    } catch (error) {
      toast.error(copy.loadError || 'Could not load');
    }
  };

  const saveWinner = async () => {
    if (!winnerModal.clientId || !winnerModal.reason.trim()) {
      toast.error(copy.reasonRequired || 'Select a winner and provide a reason');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `/api/admin/promotions/cycles/${winnerModal.cycle.id}/winner`,
        { client_id: Number(winnerModal.clientId), reason: winnerModal.reason },
        { withCredentials: true },
      );
      setWinnerModal((prev) => ({ ...prev, open: false }));
      toast.success(copy.saved || 'Saved');
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.error || copy.saveError || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append('image', file);
    const response = await axios.post('/api/admin/promotions/images', form, { withCredentials: true });
    return response.data?.image_url;
  };

  if (loading) return <FullPageSkeleton />;

  const pm = promoModal.data;
  const cm = cycleModal.data;

  return (
    <div className="space-y-6 xl:space-y-7">
      <PageHeader
        eyebrow="Elevate Hispano"
        icon={TrophyIcon}
        title={copy.adminTitle || 'Promotions'}
        subtitle={copy.adminSubtitle || 'Manage raffle campaigns, periods and winners.'}
        action={(
          <Button type="button" variant="primary" size="compact" onClick={openCreatePromotion} icon={PlusIcon} className="justify-center">
            {copy.newPromotion || 'New promotion'}
          </Button>
        )}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: copy.statPromotions || 'Promotions', value: stats.total },
          { label: copy.statActive || 'Active', value: stats.active },
          { label: copy.statTickets || 'Tickets', value: stats.tickets },
          { label: copy.statWinners || 'Winners', value: stats.winners },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04, ease }}
            className="metric-tile"
          >
            <p className="stat-label">{stat.label}</p>
            <p className="metric-tile-value text-secondary-900">{stat.value}</p>
          </motion.div>
        ))}
      </div>

      {promotions.length === 0 ? (
        <EmptyState icon={TrophyIcon} title={copy.noPromotions || 'No promotions'} description={copy.noPromotionsDescription} />
      ) : (
        <div className="space-y-5">
          {promotions.map((promotion) => (
            <motion.section
              key={promotion.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ease }}
              className="card-premium overflow-hidden"
            >
              <div className="flex flex-col gap-4 border-b border-secondary-100 p-5 sm:flex-row sm:items-center">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-secondary-50">
                  {promotion.image_url ? (
                    <img src={promotion.image_url} alt="" className="h-full w-full object-contain p-1" />
                  ) : (
                    <TrophyIcon className="h-7 w-7 text-orange-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-secondary-900">{promotion.title}</h3>
                    <span className={`rounded-full px-2.5 py-0.5 text-2xs font-bold uppercase tracking-wide ring-1 ${promotion.is_active ? 'bg-success-100 text-success-700 ring-success-200' : 'bg-secondary-100 text-secondary-500 ring-secondary-200'}`}>
                      {promotion.is_active ? (copy.active || 'Active') : (copy.inactive || 'Inactive')}
                    </span>
                    {promotion.exclusive_client_id ? (
                      <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-2xs font-bold uppercase tracking-wide text-primary-700 ring-1 ring-primary-200">
                        {copy.exclusive || 'Exclusive'}: {promotion.exclusive_client_display || promotion.exclusive_client_email}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-0.5 text-sm text-secondary-600">
                    {promotion.prize_name} · {promotion.required_referrals} {copy.referralsShort || 'refs'}
                    {promotion.count_partners ? ` · +${copy.partnersShort || 'partners'}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="secondary" size="compact" onClick={() => openEditPromotion(promotion)} icon={PencilSquareIcon}>
                    {copy.edit || 'Edit'}
                  </Button>
                  <Button type="button" variant="secondary" size="compact" onClick={() => openCycleModal(promotion)} icon={PlusIcon}>
                    {copy.addCycle || 'Add period'}
                  </Button>
                  <Button type="button" variant="secondary" size="compact" onClick={() => deletePromotion(promotion)} icon={TrashIcon}>
                    {copy.delete || 'Delete'}
                  </Button>
                </div>
              </div>

              <div className="divide-y divide-secondary-100">
                {(promotion.cycles || []).map((cycle) => (
                  <div key={cycle.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2.5 py-0.5 text-2xs font-bold uppercase tracking-wide ring-1 ${statusBadge(cycle)}`}>
                          {cycleStatusLabel(cycle, copy)}
                        </span>
                        <p className="text-sm font-semibold text-secondary-900">{cycle.label}</p>
                      </div>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-secondary-500">
                        <span><ClockIcon className="mr-1 inline h-3.5 w-3.5" />{cycle.starts_on || toDateInput(cycle.starts_at)} → {cycle.ends_on || toDateInput(cycle.ends_at)}</span>
                        <span><TrophyIcon className="mr-1 inline h-3.5 w-3.5" />{copy.fieldDraw || 'Draw'}: {cycle.draw_on || toDateInput(cycle.draw_at)}</span>
                        <span><TicketIcon className="mr-1 inline h-3.5 w-3.5" />{cycle.ticket_count} {copy.ticketsShort || 'tickets'}</span>
                        {cycle.winner_display ? <span><SparklesIcon className="mr-1 inline h-3.5 w-3.5" />{cycle.winner_display}</span> : null}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" size="compact" onClick={() => openParticipants(cycle)} icon={UserGroupIcon}>
                        {copy.participants || 'Participants'}
                      </Button>
                      <Button type="button" variant="secondary" size="compact" onClick={() => openWinnerModal(cycle)} icon={SparklesIcon}>
                        {copy.setWinner || 'Set winner'}
                      </Button>
                      {(cycle.computed_status === 'open' || cycle.computed_status === 'due') ? (
                        <Button type="button" variant="primary" size="compact" onClick={() => forceDraw(cycle)} icon={TrophyIcon}>
                          {copy.draw || 'Draw'}
                        </Button>
                      ) : null}
                      <Button type="button" variant="secondary" size="compact" onClick={() => openCycleModal(promotion, cycle)} icon={PencilSquareIcon}>
                        {copy.edit || 'Edit'}
                      </Button>
                      <Button type="button" variant="secondary" size="compact" onClick={() => deleteCycle(promotion, cycle)} icon={TrashIcon}>
                        {copy.delete || 'Delete'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          ))}
        </div>
      )}

      {audits.length > 0 ? (
        <section className="space-y-3 border-t border-secondary-200/80 pt-6">
          <p className="eyebrow">{copy.auditTitle || 'Draw audit'}</p>
          <div className="card-premium divide-y divide-secondary-100">
            {audits.slice(0, 10).map((entry) => (
              <div key={entry.id} className="flex flex-col gap-1 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                <span className="font-medium text-secondary-800">
                  {entry.action === 'winner_override' ? (copy.override || 'Manual winner') : (copy.draw || 'Draw')}
                  {entry.cycle_label ? ` · ${entry.cycle_label}` : ''}
                  {entry.winner_display ? ` · ${entry.winner_display}` : ''}
                </span>
                <span className="text-xs text-secondary-500">
                  {entry.admin_username || '—'} · {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                  {entry.reason ? ` · ${entry.reason}` : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Promotion modal */}
      <Modal
        isOpen={promoModal.open}
        onClose={() => setPromoModal((prev) => ({ ...prev, open: false }))}
        title={promoModal.mode === 'edit' ? (copy.editPromotion || 'Edit promotion') : (copy.newPromotion || 'New promotion')}
        size="large"
        footer={(
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setPromoModal((prev) => ({ ...prev, open: false }))}>{copy.cancel || 'Cancel'}</Button>
            <Button variant="primary" onClick={savePromotion} disabled={saving}>{copy.save || 'Save'}</Button>
          </div>
        )}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label={copy.fieldTitle || 'Title'} className="sm:col-span-2">
            <Input value={pm.title} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, title: e.target.value } }))} />
          </FormField>
          <FormField label={copy.fieldPrize || 'Prize name'}>
            <Input value={pm.prize_name} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, prize_name: e.target.value } }))} />
          </FormField>
          <FormField label={copy.fieldPrizeType || 'Prize type'}>
            <select className="input-field" value={pm.prize_type} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, prize_type: e.target.value } }))}>
              {PRIZE_TYPES.map((type) => <option key={type} value={type}>{type === 'cash' ? (copy.prizeCash || 'Cash') : (copy.prizePhysical || 'Physical')}</option>)}
            </select>
          </FormField>
          <FormField label={copy.fieldRequired || 'Referrals required'}>
            <Input type="number" min="1" value={pm.required_referrals} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, required_referrals: e.target.value } }))} />
          </FormField>
          <FormField label={copy.fieldMinState || 'Minimum referral state'}>
            <select className="input-field" value={pm.min_referral_state} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, min_referral_state: e.target.value } }))}>
              {STATES.map((state) => <option key={state} value={state}>{state}</option>)}
            </select>
          </FormField>
          <FormField label={copy.fieldValue || 'Prize value (cents)'}>
            <Input type="number" min="0" value={pm.prize_value_cents} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, prize_value_cents: e.target.value } }))} />
          </FormField>
          <FormField label={copy.fieldSubtitle || 'Subtitle'} className="sm:col-span-2">
            <Input value={pm.subtitle} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, subtitle: e.target.value } }))} />
          </FormField>
          <FormField label={copy.fieldDescription || 'Description'} className="sm:col-span-2">
            <Input as="textarea" rows={3} value={pm.description} onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, description: e.target.value } }))} />
          </FormField>
          <FormField label={copy.fieldImage || 'Prize image'} className="sm:col-span-2">
            <div className="flex items-center gap-3">
              {pm.image_url ? <img src={pm.image_url} alt="" className="h-14 w-14 rounded-xl object-contain ring-1 ring-secondary-200" /> : <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-secondary-50"><PhotoIcon className="h-6 w-6 text-secondary-400" /></span>}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const url = await uploadImage(file);
                    setPromoModal((p) => ({ ...p, data: { ...p.data, image_url: url } }));
                  } catch (error) {
                    toast.error(copy.saveError || 'Upload failed');
                  }
                }}
                className="text-sm text-secondary-600"
              />
            </div>
          </FormField>
          <FormField
            label={copy.exclusiveClient || 'Exclusive client (email)'}
            hint={copy.exclusiveClientHint || 'Leave empty so all clients can see and join.'}
            className="sm:col-span-2"
          >
            <Input
              type="email"
              placeholder="cliente@correo.com"
              value={pm.exclusive_client_email}
              onChange={(e) => setPromoModal((p) => ({ ...p, data: { ...p.data, exclusive_client_email: e.target.value } }))}
            />
          </FormField>
          <div className="flex items-center justify-between rounded-2xl bg-secondary-50 px-4 py-3">
            <span className="text-sm font-medium text-secondary-700">{copy.countPartners || 'Count partners'}</span>
            <Switch checked={pm.count_partners} onChange={(v) => setPromoModal((p) => ({ ...p, data: { ...p.data, count_partners: v } }))} aria-label={copy.countPartners || 'Count partners'} />
          </div>
          <div className="flex items-center justify-between rounded-2xl bg-secondary-50 px-4 py-3">
            <span className="text-sm font-medium text-secondary-700">{copy.active || 'Active'}</span>
            <Switch checked={pm.is_active} onChange={(v) => setPromoModal((p) => ({ ...p, data: { ...p.data, is_active: v } }))} aria-label={copy.active || 'Active'} />
          </div>
        </div>
      </Modal>

      {/* Cycle modal */}
      <Modal
        isOpen={cycleModal.open}
        onClose={() => setCycleModal((prev) => ({ ...prev, open: false }))}
        title={copy.cycleModalTitle || 'Period'}
        footer={(
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCycleModal((prev) => ({ ...prev, open: false }))}>{copy.cancel || 'Cancel'}</Button>
            <Button variant="primary" onClick={saveCycle} disabled={saving}>{copy.save || 'Save'}</Button>
          </div>
        )}
      >
        <div className="grid grid-cols-1 gap-4">
          <FormField label={copy.fieldLabel || 'Label'}>
            <Input value={cm.label} onChange={(e) => setCycleModal((p) => ({ ...p, data: { ...p.data, label: e.target.value } }))} />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label={copy.fieldStart || 'Start'}>
              <DateInput value={cm.starts_at} onChange={(e) => setCycleModal((p) => ({ ...p, data: { ...p.data, starts_at: e.target.value } }))} />
            </FormField>
            <FormField label={copy.fieldEnd || 'End'}>
              <DateInput value={cm.ends_at} onChange={(e) => setCycleModal((p) => ({ ...p, data: { ...p.data, ends_at: e.target.value } }))} />
            </FormField>
          </div>
          <FormField
            label={copy.fieldDraw || 'Draw date'}
            hint={copy.fieldDrawHint || 'Leave empty to draw at the end date.'}
          >
            <DateInput value={cm.draw_at} onChange={(e) => setCycleModal((p) => ({ ...p, data: { ...p.data, draw_at: e.target.value } }))} />
          </FormField>
        </div>
      </Modal>

      {/* Participants modal */}
      <Modal
        isOpen={participantsModal.open}
        onClose={() => setParticipantsModal({ open: false, cycle: null, rows: [] })}
        title={copy.participants || 'Participants'}
        size="large"
      >
        {participantsModal.rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-secondary-500">{copy.noParticipants || 'No tickets yet.'}</p>
        ) : (
          <div className="divide-y divide-secondary-100">
            {participantsModal.rows.map((row) => (
              <div key={row.ticket_id} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium text-secondary-800">{row.client_name}</span>
                <span className="font-mono text-xs text-secondary-500">{row.ticket_code}</span>
                <span className="text-xs text-secondary-500">{row.referrals_at_award} {copy.referralsShort || 'refs'}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Winner modal */}
      <Modal
        isOpen={winnerModal.open}
        onClose={() => setWinnerModal((prev) => ({ ...prev, open: false }))}
        title={copy.setWinner || 'Set winner'}
        footer={(
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setWinnerModal((prev) => ({ ...prev, open: false }))}>{copy.cancel || 'Cancel'}</Button>
            <Button variant="primary" onClick={saveWinner} disabled={saving}>{copy.save || 'Save'}</Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <FormField label={copy.winner || 'Winner'}>
            <select className="input-field" value={winnerModal.clientId} onChange={(e) => setWinnerModal((p) => ({ ...p, clientId: e.target.value }))}>
              <option value="">—</option>
              {(winnerModal.rows || []).map((row) => (
                <option key={row.ticket_id} value={row.client_id}>{row.client_name} ({row.ticket_code})</option>
              ))}
            </select>
          </FormField>
          <FormField label={copy.reason || 'Reason'}>
            <Input value={winnerModal.reason} onChange={(e) => setWinnerModal((p) => ({ ...p, reason: e.target.value }))} />
          </FormField>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={async () => {
          const action = confirm?.onConfirm;
          setConfirm(null);
          if (action) await action();
        }}
      />
    </div>
  );
}

export default AdminPromotionsView;
